import React, { useContext } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import SectionColumn from "../sections/SectionColumn";
import FlexSectionColumn from "../section_columns/FlexSectionColumn";
import NewSectionColumn from "../section_columns/NewSectionColumn";
import arePropsEqual from "../../utils/arePropsEqual";
import SectionSpacingHandle from "../sections/SectionSpacingHandle";
import BuilderSkeletonPlaceholder from "../variants/BuilderSkeletonPlaceholder";
import {
  toFlexColumnDescriptors,
  getFlexSectionColumns,
  flexRowBreakEvery,
} from "../../utils/builderUtils";
import { buildSectionStylesheet } from "./SectionStyleResolver";

const Section = (props) => {
  let cta = props.cta;
  let variant = props.variant;
  let section = props.section;

  if (section.placeholder == true) {
    return (
      <div
        id={"section" + section.id}
        className="cf-section pointer-events-none"
        data-section-id={section.id}
        data-pos={section.position}
        data-step-id={section.step_id}
        data-element-id={section.element_id ? section.element_id : ""}
        data-object-type="sections"
        data-object-id={section.id}
      >
        <BuilderSkeletonPlaceholder object_type="sections" />
      </div>
    );
  }

  const sectionOptions = section.options || {};
  let options = sectionOptions.structure ?? sectionOptions["structure"];
  let important = props.builder !== true ? " !important" : "";
  let selected;
  const classes = style.classes(cta, section);
  const stylesheet = buildSectionStylesheet(
    props.builder,
    props.device,
    cta,
    variant,
    section
  );
  let contextData;

  if (props.builder == true) {
    [contextData] = useContext(VariantContextData);
  }

  const showBuilderSpacingHandles =
    props.builder == true &&
    contextData.selected_object_type == "sections" &&
    contextData.selected_object_id == section.id &&
    !contextData.selected_object_collapse;

  const isFlex = section.layout_type === "flex";
  let columns;
  let flex_section_columns = [];
  if (isFlex) {
    flex_section_columns = getFlexSectionColumns(section, variant);
    columns = toFlexColumnDescriptors(flex_section_columns);
  } else {
    columns = [
      { name: "first", position: 1 },
      { name: "second", position: 2 },
      { name: "third", position: 3 },
      { name: "fourth", position: 4 },
    ];
  }

  // An explicit columns-per-row-mobile always wins. Without one, mobile
  // responsive (default on) stacks flex columns one per row on phones;
  // toggled off, mobile follows the desktop per-row count.
  const desktopBreakEvery = flexRowBreakEvery(section, "width");
  const mobileBreakEvery = flexRowBreakEvery(section, "width-mobile");

  const rowBreaksAfter = (index) => {
    if (index >= flex_section_columns.length - 1) return [];
    const breaks = [];
    const order = (columns[index] && columns[index].position) || index + 1;
    if (desktopBreakEvery && (index + 1) % desktopBreakEvery === 0) {
      breaks.push(
        <div
          key={`flex-break-d-${section.id}-${index}`}
          className="cf-flex-row-break cf-flex-row-break-desktop"
          style={{ order }}
        />
      );
    }
    if (mobileBreakEvery && (index + 1) % mobileBreakEvery === 0) {
      breaks.push(
        <div
          key={`flex-break-m-${section.id}-${index}`}
          className="cf-flex-row-break cf-flex-row-break-mobile"
          style={{ order }}
        />
      );
    }
    return breaks;
  };

  const embedYoutubeLink = () => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = options["background-video"].match(regExp);
    const youtubeId = match && match[2].length === 11 ? match[2] : null;

    return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&loop=1&mute=1&playlist=${youtubeId}`;
  };

  const embedVimeoLink = () => {
    const regExp = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
    const match = options["background-video"].match(regExp);
    const vimeoId = match[1];

    return `https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&color=ED8374&title=0&byline=0&portrait=0&badge=0`;
  };

  const embedWistiaLink = () => {
    const regExp = /(?:wistia)\..*(?:medias\/|embed\/)([^#&? ]*)/i;
    const match = options["background-video"].match(regExp);
    const wistiaId = match[1];

    return `https://fast.wistia.net/embed/iframe/${wistiaId}`;
  };

  let backgroundVideoUrl = "";
  if (
    options["background-video"] &&
    options["background-video"].includes("youtube")
  ) {
    backgroundVideoUrl = embedYoutubeLink();
  } else if (
    options["background-video"] &&
    options["background-video"].includes("vimeo")
  ) {
    backgroundVideoUrl = embedVimeoLink();
  } else if (
    options["background-video"] &&
    options["background-video"].includes("wistia")
  ) {
    backgroundVideoUrl = embedWistiaLink();
  }

  return (
    <React.Fragment>
      <div
        id={"section" + section.id}
        className={classes.outer}
        section-id={section.id}
        data-section-id={section.id}
        data-pos={section.position}
        step-id={section.step_id}
        data-step-id={section.step_id}
        element-id={section.element_id ? section.element_id : ""}
        data-element-id={section.element_id ? section.element_id : ""}
        data-object-type="sections"
        data-object-id={section.id}
      >
        <div className="cf-section-overlay-outer" data-section-id={section.id}>
          <div
            className="cf-video-background-container"
            data-section-id={section.id}
            section-id={section.id}
          >
            {options["background-video"] && (
              <iframe
                className="cf-video-background-iframe"
                src={backgroundVideoUrl}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy={
                  options["background-video"].indexOf("youtube") > -1
                    ? "strict-origin-when-cross-origin"
                    : null
                }
              ></iframe>
            )}
          </div>
          <div
            className="cf-section-overlay"
            section-id={section.id}
            data-section-id={section.id}
            data-element-id={section.element_id}
          ></div>
        </div>
        <div
          className={`${classes.inner}${isFlex ? " cf-section-flex" : ""}`}
          section-id={section.id}
          data-section-id={section.id}
          data-mobile-responsive={
            options["mobile-responsive"] ? options["mobile-responsive"] : "true"
          }
          style={
            isFlex
              ? {
                  display: "flex",
                  flexDirection: "row",
                  // Always wrap: columns take their basis (columns-per-row share
                  // or a px/% override) and overflow drops to the next row.
                  flexWrap: "wrap",
                  width: "100%",
                }
              : undefined
          }
        >
          {isFlex
            ? flex_section_columns.length > 0
              ? flex_section_columns.flatMap((section_column, i) => [
                  <FlexSectionColumn
                    key={`section-${section.id}-flex-column-${section_column.id}`}
                    column={columns[i]}
                    section_column={section_column}
                    {...props}
                  />,
                  ...rowBreaksAfter(i),
                ])
              : // Flex section with no columns (all deleted, or a partial/failed
                // upgrade): keep a clickable placeholder in the builder so the
                // section can still be selected and a column added, instead of
                // rendering an empty, unselectable block. Live output renders
                // nothing rather than an editor affordance.
                props.builder == true && (
                  <div
                    className="cf-col cf-flex-column cf-col-md-12 cf-center"
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      float: "none",
                      padding: "25px",
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      document.dispatchEvent(
                        new CustomEvent("showPopover", {
                          detail: {
                            object_type: "sections",
                            object_id: section.id,
                            tab: section.element_id ? "Row" : "Section",
                            object: section,
                            anchorPosition: { x: e.clientX, y: e.clientY },
                          },
                        })
                      );
                    }}
                  >
                    <NewSectionColumn
                      section={section}
                      canvasPlaceholder={true}
                    />
                  </div>
                )
            : columns.map((column, i) => {
                return (
                  <SectionColumn
                    key={"section-" + section.id + "-column-" + column.position}
                    column={column}
                    {...props}
                  ></SectionColumn>
                );
              })}
        </div>
        <style dangerouslySetInnerHTML={{ __html: stylesheet }} />
        {showBuilderSpacingHandles && (
          <React.Fragment>
            <SectionSpacingHandle
              key={`section-${section.id}-spacer-top`}
              {...props}
              direction="top"
            ></SectionSpacingHandle>
            <SectionSpacingHandle
              key={`section-${section.id}-spacer-bottom`}
              {...props}
              direction="bottom"
            ></SectionSpacingHandle>
            <SectionSpacingHandle
              key={`section-${section.id}-spacer-left`}
              {...props}
              direction="left"
            ></SectionSpacingHandle>
            <SectionSpacingHandle
              key={`section-${section.id}-spacer-right`}
              {...props}
              direction="right"
            ></SectionSpacingHandle>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

const style = {
  classes: (cta, section) => {
    let options = section.options["structure"];
    let outer = "cf-row cf-section cf-outer-center";

    if (section.loaded == false) {
      outer += " opacity-50 pointer-events-none";
    }

    outer += options["hide-desktop"] == "true" ? " cf-hide-desktop" : "";

    outer += options["hide-mobile"] == "true" ? " cf-hide-mobile" : "";

    if (options["background-position"] !== "custom") {
      outer += options["background-position"]
        ? " cf-background-position-" + options["background-position"]
        : " cf-background-position-top-center";
    }

    if (options["background-size"] !== "custom") {
      outer += options["background-size"]
        ? " cf-background-size-" + options["background-size"]
        : " cf-background-size-cover";
    }

    outer += options["custom-class"] ? " " + options["custom-class"] : "";

    if (section.is_template == true) {
      outer += " cf-cta-container";
    }

    let inner = "cf-section-container cf-row cf-outer-center";

    inner +=
      options["column-container"] == "page" ||
      (!options["column-container"] && cta.cta_type == "page")
        ? " cf-section-page-width"
        : "";

    return { outer: outer, inner: inner };
  },
};

export default React.memo(Section, arePropsEqual);
